function formatMoney(n) {
  const x = Number(n)
  if (!Number.isFinite(x)) return '$0.00'
  return '$' + x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function collectionItemIsOpen(r) {
  if (!r) return false
  if (r.payment_status != null || r.deleted_at != null || r.bad_debt != null || r.pa_status) {
    if (r.deleted_at != null) return false
    if ((r.pa_status || 'submitted') !== 'submitted') return false
    if (r.bad_debt === true) return false
    if (r.payment_status === 'paid') return false
    const due = r.amount_due != null ? Number(r.amount_due) : (Number(r.amount_cents) || 0) / 100
    return due - (Number(r.amount_paid) || 0) > 0
  }
  if (r.paid === true || r.closed === true) return false
  return true
}

export function ownerNeedleFromQuestion(question) {
  const q = String(question || '')
  const m = q.match(/when will\s+(.+?)\s+pay/i)
  if (m) return String(m[1]).trim()
  if (/taj/i.test(q)) return 'taj'
  return ''
}

export function isOwnerRow(r, needle) {
  const n = String(needle || '').toLowerCase()
  if (!n) return false
  return [r.owner_name, r.project_name, r.project, r.label]
    .some((x) => String(x || '').toLowerCase().includes(n))
}

function isLateItem(r) {
  return r && (r.status === 'late' || r.include_in_base === false)
}

function withOwner(rows, meta) {
  const byId = new Map((meta || []).map((m) => [Number(m.pay_app_id || m.source_id), m]))
  return (rows || []).map((r) => {
    const m = byId.get(Number(r.pay_app_id || r.source_id))
    if (!m) return r
    return {
      ...r,
      owner_name: r.owner_name || m.owner_name,
      owner_id: r.owner_id != null ? r.owner_id : m.owner_id,
      project_name: r.project_name || m.project_name,
    }
  })
}

export function receivableSourceLines(version, needle) {
  const payload = (version && version.payload) || {}
  const lines = withOwner(payload.lines || [], payload.receipt_meta || [])
  return lines.filter((l) => {
    if (l.direction && l.direction !== 'in') return false
    if (l.source_type === 'vendor_invoice') return false
    if (l.source_type !== 'pay_app' && l.source_type !== 'retainage') return false
    if (needle && !isOwnerRow(l, needle)) return false
    return true
  })
}

export function shapeOwnerPayAnswer(version, questionOrNeedle) {
  const payload = (version && version.payload) || version || {}
  const weeks = payload.weeks || []
  const needle = ownerNeedleFromQuestion(questionOrNeedle) || String(questionOrNeedle || 'taj')
  const items = withOwner(payload.collections?.items || payload.collections || [], payload.receipt_meta || [])
  const thisWeekStart = weeks[0]?.week_start
  const open = (Array.isArray(items) ? items : []).filter((r) => isOwnerRow(r, needle)).filter(collectionItemIsOpen)
  const late = open.filter(isLateItem)
  const base = open.filter((r) => !isLateItem(r))
  const thisWeek = base.filter((r) => r.week_start && r.week_start === thisWeekStart)
  const later = base.filter((r) => !r.week_start || r.week_start !== thisWeekStart)
  const sumCents = (rows) => rows.reduce((s, r) => s + (Number(r.amount_cents) || 0), 0)
  const thisWeekTotal = sumCents(thisWeek) / 100
  const byWeek = new Map()
  for (const r of later) {
    const key = r.predicted_paid_at || r.week_start || 'later'
    byWeek.set(key, (byWeek.get(key) || 0) + (Number(r.amount_cents) || 0))
  }
  const monthDayShort = (iso) => {
    const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number)
    if (!y || !m || !d) return String(iso || '')
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  const laterParts = [...byWeek.entries()].map(([key, cents]) => (
    `${formatMoney(cents / 100)} the week of ${monthDayShort(key)}`
  ))
  const ownerLabel = needle.charAt(0).toUpperCase() + needle.slice(1)
  const thisFmt = formatMoney(thisWeekTotal)
  let yesNo
  if (!open.length) {
    yesNo = `No open ${ownerLabel} pay apps.`
  } else if (thisWeek.length && laterParts.length) {
    yesNo = `${ownerLabel} is expected to pay ${thisFmt} this week and ${laterParts.join(' and ')}`
  } else if (thisWeek.length) {
    yesNo = `${ownerLabel} is expected to pay ${thisFmt} this week`
  } else if (laterParts.length) {
    yesNo = `${ownerLabel} is expected to pay ${laterParts.join(' and ')}`
  } else {
    yesNo = `${ownerLabel} has no expected collections inside the usual window`
  }
  const reason = [
    ...base.map((r) => {
      const name = r.project_name || r.project || ownerLabel
      const pa = r.pay_app_id || r.pay_app_number
      const amt = r.amount?.formatted || formatMoney((Number(r.amount_cents) || 0) / 100)
      const when = r.likely_label || ''
      return `- ${name} · Pay app ${pa} · ${amt}${when ? ` · ${when}` : ''}`
    }),
    ...late.map((r) => {
      const name = r.project_name || r.project || ownerLabel
      const pa = r.pay_app_id || r.pay_app_number
      const amt = r.amount?.formatted || formatMoney((Number(r.amount_cents) || 0) / 100)
      return `- ${name} · Pay app ${pa} · ${amt} · Late`
    }),
  ].join('\n')
  return {
    yesNo,
    amount: null,
    date: null,
    reason: reason || `No open ${ownerLabel} pay apps are in the collections list.`,
    thisWeekTotal,
    offerFollowUp: late.length > 0,
    sourceLines: receivableSourceLines(version.payload ? version : { payload: version }, needle),
  }
}

export function shapeTajPayAnswer(items, thisWeekStart, extra) {
  return shapeOwnerPayAnswer({
    payload: {
      weeks: [{ week_start: thisWeekStart }],
      collections: { items },
      collect_now: extra?.collect_now || [],
      lines: extra?.lines || [],
    },
  }, extra?.needle || 'taj')
}
